import React, { useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { PGET } from "helpers/api";
import { formatMoney, formatDate } from "helpers/helpers";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import uzbekistanRegions from "./uzbekistanRegions.json";

const DEFAULT_CENTER = [41.2995, 69.2401];
const DEFAULT_ZOOM = 6;

// Savdo nuqtasidan [lat, lng] ni ajratib olish
const getLatLng = (item) => {
	let latitude, longitude;
	const addressParts = item.address?.split(",").map((p) => p.trim());
	if (
		addressParts &&
		!isNaN(parseFloat(addressParts[0])) &&
		!isNaN(parseFloat(addressParts[1]))
	) {
		[latitude, longitude] = addressParts.map(Number);
	} else {
		latitude = item.gpsPointX;
		longitude = item.gpsPointY;
	}
	if (!latitude || !longitude) return null;
	return [Number(latitude), Number(longitude)];
};

// Ray-casting: nuqta ko'pburchak halqasi ichidami
const pointInRing = (x, y, ring) => {
	let inside = false;
	for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
		const xi = ring[i][0], yi = ring[i][1];
		const xj = ring[j][0], yj = ring[j][1];
		const intersect = ((yi > y) !== (yj > y)) && (x < ((xj - xi) * (y - yi)) / (yj - yi) + xi);
		if (intersect) inside = !inside;
	}
	return inside;
};

// Nuqta (lng, lat) Polygon/MultiPolygon geometriyasi ichidami (teshiklarni hisobga olib)
const pointInGeometry = (lng, lat, geometry) => {
	const polys = geometry.type === "Polygon" ? [geometry.coordinates] : geometry.coordinates;
	for (const poly of polys) {
		if (pointInRing(lng, lat, poly[0])) {
			let inHole = false;
			for (let h = 1; h < poly.length; h++) {
				if (pointInRing(lng, lat, poly[h])) { inHole = true; break; }
			}
			if (!inHole) return true;
		}
	}
	return false;
};

// Koordinata qaysi viloyatga tushishini aniqlaymiz (regionId yoki null)
const findRegionId = (lat, lng) => {
	for (const f of uzbekistanRegions.features) {
		if (pointInGeometry(lng, lat, f.geometry)) return f.properties.regionId;
	}
	return null;
};

// Marker popup'i uchun chiroyli HTML
const buildPopupHtml = (item) => {
	const row = (label, value, opts = {}) => `
		<div style="display:flex;justify-content:space-between;gap:12px;padding:6px 0;border-top:1px solid #f2f4f7">
			<span style="color:#667085;font-size:12.5px">${label}</span>
			<span style="color:${opts.color || "#101828"};font-size:12.5px;font-weight:${opts.bold ? 700 : 500};text-align:right">${value}</span>
		</div>`;

	const badge = item.activated
		? `<span style="background:#ecfdf3;color:#067647;font-size:11px;font-weight:600;padding:3px 9px;border-radius:999px;white-space:nowrap">● Faol</span>`
		: `<span style="background:#fef3f2;color:#b42318;font-size:11px;font-weight:600;padding:3px 9px;border-radius:999px;white-space:nowrap">● Nofaol</span>`;

	const phone = item.phone
		? `<a href="tel:${item.phone}" style="color:#175cd3;text-decoration:none">${item.phone}</a>`
		: `<span style="color:#98a2b3">—</span>`;

	return `
		<div style="min-width:248px;font-family:inherit">
			<div style="display:flex;justify-content:space-between;align-items:center;gap:10px;margin-bottom:2px">
				<span style="font-size:15px;font-weight:700;color:#101828">${item.name ?? "—"}</span>
				${badge}
			</div>
			<div style="color:#98a2b3;font-size:12px;margin-bottom:6px">@${item.ownerLogin ?? "—"}</div>
			${row("Telefon", phone)}
			${row("Tarif", `${formatMoney(item.tariff)} so'm`)}
			${row("Oylik to'lov", `${formatMoney(item.monthlyPayment)} so'm`, { bold: true })}
			${row("Balans", `${formatMoney(item.balance)} so'm`, { color: Number(item.balance) < 0 ? "#b42318" : "#101828" })}
			${row("To'lov muddati", `${item.paidFrom ?? "—"} → ${item.paidUntil ?? "—"}`)}
			${row("Ro'yxatdan o'tgan", formatDate(item.createdDate))}
			${row("Manzil", item.address ?? "—")}
		</div>`;
};

const REGIONS = [
	{ id: 14, name: "Qoraqalpog'iston Resp." },
	{ id: 1, name: "Andijon viloyati" },
	{ id: 2, name: "Buxoro viloyati" },
	{ id: 3, name: "Jizzax viloyati" },
	{ id: 4, name: "Qashqadaryo viloyati" },
	{ id: 5, name: "Navoiy viloyati" },
	{ id: 6, name: "Namangan viloyati" },
	{ id: 7, name: "Samarqand viloyati" },
	{ id: 8, name: "Surxondaryo viloyati" },
	{ id: 9, name: "Sirdaryo viloyati" },
	{ id: 10, name: "Toshkent shahri" },
	{ id: 11, name: "Toshkent viloyati" },
	{ id: 12, name: "Farg'ona viloyati" },
	{ id: 13, name: "Xorazm viloyati" },
];

const MapComponent = () => {
	const [map, setMap] = useState(null);
	const [markers, setMarkers] = useState([]);
	const [Clients, setClients] = useState([]);
	const [monthlyPayments, setMonthlyPayments] = useState([]);
	const [loading, setLoading] = useState(true);
	const [selectedRegion, setSelectedRegion] = useState("");
	const [startMonth, setStartMonth] = useState(null);
	const [endMonth, setEndMonth] = useState(null);
	const regionLayerRef = useRef(null);

	// posId -> regionId (koordinata bo'yicha)
	const posRegionMap = useMemo(() => {
		const m = {};
		for (const item of Clients) {
			const coords = getLatLng(item);
			m[item.id] = coords ? findRegionId(coords[0], coords[1]) : null;
		}
		return m;
	}, [Clients]);

	// Mavjud oylar ro'yxati (get-payments-by-months dan)
	const allMonthKeys = useMemo(() => {
		const set = new Set();
		monthlyPayments.forEach((row) => {
			Object.keys(row.forThisMonth || {}).forEach((k) => set.add(k));
			Object.keys(row.paidInThisMonth || {}).forEach((k) => set.add(k));
		});
		return Array.from(set).sort();
	}, [monthlyPayments]);

	// Tanlangan oy oralig'i
	const visibleMonths = useMemo(() => {
		if (!startMonth && !endMonth) return allMonthKeys;
		const startKey = startMonth ? format(startMonth, "yyyy-MM") : allMonthKeys[0];
		const endKey = endMonth ? format(endMonth, "yyyy-MM") : allMonthKeys[allMonthKeys.length - 1];
		return allMonthKeys.filter((k) => k >= startKey && k <= endKey);
	}, [startMonth, endMonth, allMonthKeys]);

	// Viloyatlar kesimida statistika (mijozlar soni + tanlangan oylardagi tushum)
	const { regionRows, totals } = useMemo(() => {
		const empty = () => ({ count: 0, active: 0, forMonth: 0, paidIn: 0 });
		const acc = {};
		const unknown = empty();
		const bucketOf = (rid) => (rid ? (acc[rid] || (acc[rid] = empty())) : unknown);

		// Mijozlar soni va faollik
		for (const item of Clients) {
			const bucket = bucketOf(posRegionMap[item.id]);
			bucket.count += 1;
			if (item.activated) bucket.active += 1;
		}

		// Tanlangan oylardagi tushum (forThisMonth = hisoblangan, paidInThisMonth = to'langan)
		for (const row of monthlyPayments) {
			const bucket = bucketOf(posRegionMap[row.posId]);
			for (const mk of visibleMonths) {
				bucket.forMonth += Number(row.forThisMonth?.[mk]) || 0;
				bucket.paidIn += Number(row.paidInThisMonth?.[mk]) || 0;
			}
		}

		const regionRows = REGIONS.map((r) => ({
			id: r.id,
			name: r.name,
			...(acc[r.id] || empty()),
		})).sort((a, b) => b.paidIn - a.paidIn);

		if (unknown.count || unknown.paidIn || unknown.forMonth) {
			regionRows.push({ id: "unknown", name: "Aniqlanmagan", ...unknown });
		}

		const totals = regionRows.reduce(
			(t, r) => ({
				count: t.count + r.count,
				active: t.active + r.active,
				forMonth: t.forMonth + r.forMonth,
				paidIn: t.paidIn + r.paidIn,
			}),
			empty()
		);

		return { regionRows, totals };
	}, [Clients, monthlyPayments, posRegionMap, visibleMonths]);

	useEffect(() => {
		const initializeMap = () => {
			const newMap = L.map("map", { crs: L.CRS.EPSG3395, scrollWheelZoom: false }).setView(DEFAULT_CENTER, DEFAULT_ZOOM);
			// Ctrl bilan g'ildirakda zoom, oddiy g'ildirakda sahifa scroll bo'lsin
			newMap.scrollWheelZoom.disable();
			newMap.on("focus", () => newMap.scrollWheelZoom.enable());
			newMap.on("blur", () => newMap.scrollWheelZoom.disable());
			L.tileLayer("https://core-renderer-tiles.maps.yandex.net/tiles?l=map&x={x}&y={y}&z={z}&scale=1&lang=uz_UZ", {
				attribution: "&copy; <a href='https://yandex.com/maps'>Yandex</a>",
				subdomains: ["01", "02", "03", "04"],
			}).addTo(newMap);
			setMap(newMap);
		};
		initializeMap();
	}, []);

	useEffect(() => {
		const getData = async () => {
			setLoading(true);
			try {
				// pos-get-all — koordinatalar bor, lekin to'lov maydonlari yo'q
				// pos-pageList — to'lov/tarif maydonlari bor. Ikkalasini id bo'yicha birlashtiramiz.
				const [allRes, payRes, monthsRes] = await Promise.all([
					PGET("/services/admin/api/pos-get-all", {}, { loader: false }),
					PGET("/services/admin/api/pos-pageList", { size: 100000 }, { loader: false }),
					PGET("/services/admin/api/get-payments-by-months", {}, { loader: false }),
				]);

				const payments = {};
				(payRes?.data || []).forEach((p) => {
					payments[p.id] = p;
				});

				const merged = (allRes?.data || []).map((item) => ({
					...(payments[item.id] || {}),
					...item,
				}));

				setClients(merged);
				setMonthlyPayments(monthsRes?.data || []);
			} finally {
				setLoading(false);
			}
		};

		getData();
	}, []);

	useEffect(() => {
		if (!map) return;

		markers.forEach((marker) => marker.remove());

		const shadowUrl = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png";

		const greenIcon = L.icon({
			iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png",
			iconSize: [25, 41],
			iconAnchor: [12, 41],
			popupAnchor: [1, -34],
			shadowUrl,
			shadowSize: [41, 41],
		});

		const redIcon = L.icon({
			iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png",
			iconSize: [25, 41],
			iconAnchor: [12, 41],
			popupAnchor: [1, -34],
			shadowUrl,
			shadowSize: [41, 41],
		});

		// Oldingi viloyat chegarasini olib tashlaymiz
		if (regionLayerRef.current) {
			regionLayerRef.current.remove();
			regionLayerRef.current = null;
		}

		// Tanlangan viloyat GeoJSON feature'ini topamiz
		const feature = selectedRegion
			? uzbekistanRegions.features.find(
				(f) => String(f.properties.regionId) === String(selectedRegion)
			)
			: null;

		// Savdo nuqtalarini filterlash — koordinatasi viloyat chegarasi ichidagilar
		const newMarkers = Clients.map((item) => {
			const coords = getLatLng(item);
			if (!coords) return null;
			const [latitude, longitude] = coords;

			// Viloyat tanlangan bo'lsa, faqat chegara ichidagilarni ko'rsatamiz
			if (feature && !pointInGeometry(longitude, latitude, feature.geometry)) {
				return null;
			}

			return L.marker([latitude, longitude], { icon: item.activated ? greenIcon : redIcon })
				.addTo(map)
				.bindPopup(buildPopupHtml(item), { maxWidth: 300, minWidth: 248 });
		}).filter(Boolean);

		setMarkers(newMarkers);

		// Viloyat chegarasini chizib, unga zoom qilamiz
		if (feature) {
			const layer = L.geoJSON(feature, {
				style: { color: "#2563eb", weight: 2, fillColor: "#3b82f6", fillOpacity: 0.08 },
			}).addTo(map);
			regionLayerRef.current = layer;
			map.fitBounds(layer.getBounds(), { padding: [20, 20] });
		} else {
			map.setView(DEFAULT_CENTER, DEFAULT_ZOOM);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [map, Clients, selectedRegion]);

	// ⬇️ Excelga yuklab olish funksiyasi
// === Helpers ===
const flattenObject = (obj, prefix = "", out = {}) => {
  if (obj === null || obj === undefined) return out;
  if (typeof obj !== "object") {
    out[prefix.replace(/\.$/, "")] = obj;
    return out;
  }

  if (Array.isArray(obj)) {
    // Massivni indeks bo‘yicha yoyib chiqamiz
    obj.forEach((v, i) => {
      flattenObject(v, `${prefix}${i}.`, out);
    });
    // Qo‘shimcha: massivni butuncha string sifatida ham saqlab qo‘yamiz (qulay ko‘rish uchun)
    out[prefix.replace(/\.$/, "")] = obj.map(v => (typeof v === "object" ? JSON.stringify(v) : v)).join("; ");
    return out;
  }

  Object.entries(obj).forEach(([k, v]) => {
    flattenObject(v, `${prefix}${k}.`, out);
  });
  return out;
};

const parseLatLng = (item) => {
  let latitude, longitude;
  const addressParts = item.address?.split(",").map((p) => p.trim());

  if (
    addressParts &&
    !isNaN(parseFloat(addressParts[0])) &&
    !isNaN(parseFloat(addressParts[1]))
  ) {
    [latitude, longitude] = addressParts.map(Number);
  } else {
    latitude = item.gpsPointX;
    longitude = item.gpsPointY;
  }

  return {
    Latitude: (latitude ?? "") === null ? "" : latitude,
    Longitude: (longitude ?? "") === null ? "" : longitude,
  };
};

// === Excel export (FULL) ===
const exportToExcel = () => {
  if (!Clients?.length) return;

  // 1) Har bir elementni flatten qilamiz va Lat/Long qo‘shamiz
  const flatRows = Clients.map((client) => {
    const base = flattenObject(client);
    const { Latitude, Longitude } = parseLatLng(client);
    return { ...base, Latitude, Longitude };
  });

  // 2) Barcha ustun kalitlarini to‘plab, yagona tartib hosil qilamiz
  const allKeysSet = new Set();
  flatRows.forEach((row) => Object.keys(row).forEach((k) => allKeysSet.add(k)));
  // Ixtiyoriy: Lat/Long ustunlarini oxirida yoki boshida ko‘rsatish
  const allKeys = Array.from(allKeysSet).filter(k => k !== "Latitude" && k !== "Longitude");
  allKeys.push("Latitude", "Longitude");

  // 3) Ustunlar bo‘yicha qiymatlarni to‘ldiramiz (bo‘sh joylarni "" bilan)
  const table = flatRows.map((row) => {
    const o = {};
    allKeys.forEach((k) => {
      let val = row[k];
      // Sana yoki raqamlar primitiv ko‘rinishda ketsin
      if (val === null || val === undefined) val = "";
      if (typeof val === "object") val = JSON.stringify(val);
      o[k] = val;
    });
    return o;
  });

  // 4) Sheet yaratamiz
  const worksheet = XLSX.utils.json_to_sheet(table, { header: allKeys });

  // 5) Avto-kenglik: har bir ustun uchun eng uzun qiymat uzunligiga moslash
  const colWidths = allKeys.map((k) => {
    const maxLen = Math.max(
      k.length,
      ...table.map((r) => String(r[k] ?? "").length)
    );
    // Haddan tashqari uzun bo‘lmasin
    return { wch: Math.min(Math.max(10, maxLen + 2), 60) };
  });
  worksheet["!cols"] = colWidths;

  // 6) Kitob va faylni yozamiz
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Hamma_ma'lumot");

  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const blob = new Blob([excelBuffer], {
    type:
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  const dt = new Date();
  const stamp = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(
    dt.getDate()
  ).padStart(2, "0")}_${String(dt.getHours()).padStart(2, "0")}${String(
    dt.getMinutes()
  ).padStart(2, "0")}`;
  saveAs(blob, `clients_full_${stamp}.xlsx`);
};



	// Tanlangan hududga mos (xaritada ko'rinadigan) mijozlar ro'yxati
	const visibleClients = useMemo(() => {
		if (!selectedRegion) return Clients;
		const feature = uzbekistanRegions.features.find(
			(f) => String(f.properties.regionId) === String(selectedRegion)
		);
		if (!feature) return Clients;
		return Clients.filter((item) => {
			const coords = getLatLng(item);
			if (!coords) return false;
			const [latitude, longitude] = coords;
			return pointInGeometry(longitude, latitude, feature.geometry);
		});
	}, [Clients, selectedRegion]);

	const totalCount = visibleClients.length;
	const activeCount = visibleClients.filter((c) => c.activated).length;
	const inactiveCount = totalCount - activeCount;
	const activePercent = totalCount ? Math.round((activeCount / totalCount) * 100) : 0;

	return (
		<div style={{ position: "relative", minHeight: loading ? "70vh" : undefined }}>
			<style>{`
				@keyframes idokon-spin { to { transform: rotate(360deg); } }
				@keyframes idokon-pulse { 0%,100% { opacity: 1; } 50% { opacity: .35; } }
			`}</style>

			{loading && (
				<div
					style={{
						position: "absolute",
						inset: 0,
						zIndex: 1000,
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "flex-start",
						paddingTop: "18vh",
						gap: 22,
						background: "rgba(255,255,255,0.92)",
						backdropFilter: "blur(2px)",
						borderRadius: 16,
					}}
				>
					<div style={{ position: "relative", width: 96, height: 96 }}>
						<div
							style={{
								position: "absolute",
								inset: 0,
								borderRadius: "50%",
								border: "8px solid #e6f7f0",
							}}
						/>
						<div
							style={{
								position: "absolute",
								inset: 0,
								borderRadius: "50%",
								border: "8px solid transparent",
								borderTopColor: "#12b76a",
								borderRightColor: "#12b76a",
								animation: "idokon-spin 0.9s linear infinite",
							}}
						/>
					</div>
					<div style={{ textAlign: "center" }}>
						<div style={{ fontSize: 18, fontWeight: 700, color: "#101828" }}>
							Ma'lumotlar yuklanmoqda
						</div>
						<div
							style={{
								fontSize: 14,
								color: "#667085",
								marginTop: 4,
								animation: "idokon-pulse 1.4s ease-in-out infinite",
							}}
						>
							Iltimos, biroz kuting…
						</div>
					</div>
				</div>
			)}

			<div
				className="d-flex justify-content-between align-items-center my-3 flex-wrap gap-3"
				style={{
					background: "#ffffff",
					border: "1px solid #eef0f3",
					borderRadius: "16px",
					padding: "18px 22px",
					boxShadow: "0 1px 3px rgba(16, 24, 40, 0.04)",
				}}
			>
				<div className="d-flex align-items-center flex-wrap gap-4">
					<div>
						<div
							style={{
								fontSize: "12px",
								fontWeight: 600,
								letterSpacing: "0.6px",
								textTransform: "uppercase",
								color: "#98a2b3",
							}}
						>
							Jami mijozlar
						</div>
						<div
							style={{
								fontSize: "34px",
								fontWeight: 700,
								lineHeight: 1.05,
								color: "#101828",
							}}
						>
							{totalCount.toLocaleString("ru-RU")}
						</div>
					</div>

					<div className="d-flex flex-wrap gap-2">
						<span
							style={{
								display: "inline-flex",
								alignItems: "center",
								gap: "8px",
								padding: "8px 14px",
								borderRadius: "999px",
								background: "#ecfdf3",
								color: "#067647",
								fontWeight: 600,
								fontSize: "14px",
							}}
						>
							<span style={{ width: 8, height: 8, borderRadius: "50%", background: "#12b76a" }} />
							Faol: {activeCount.toLocaleString("ru-RU")}
							<span style={{ color: "#85cda5", fontWeight: 500 }}>({activePercent}%)</span>
						</span>
						<span
							style={{
								display: "inline-flex",
								alignItems: "center",
								gap: "8px",
								padding: "8px 14px",
								borderRadius: "999px",
								background: "#fef3f2",
								color: "#b42318",
								fontWeight: 600,
								fontSize: "14px",
							}}
						>
							<span style={{ width: 8, height: 8, borderRadius: "50%", background: "#f04438" }} />
							Nofaol: {inactiveCount.toLocaleString("ru-RU")}
							<span style={{ color: "#e29b94", fontWeight: 500 }}>({100 - activePercent}%)</span>
						</span>
					</div>
				</div>

				<div className="d-flex align-items-center gap-2">
					<select
						className="form-select form-select-sm"
						style={{ minWidth: "220px", borderRadius: "10px" }}
						value={selectedRegion}
						onChange={(e) => setSelectedRegion(e.target.value)}
					>
						<option value="">Barcha hududlar</option>
						{REGIONS.map((region) => (
							<option key={region.id} value={region.id}>
								{region.name}
							</option>
						))}
					</select>
					<button
						onClick={exportToExcel}
						className="btn btn-success btn-sm"
						style={{ borderRadius: "10px", whiteSpace: "nowrap" }}
					>
						📥 Excelga yuklab olish
					</button>
				</div>
			</div>

			<div style={{ width: "100%", borderRadius: "50px", backgroundColor: "white", height: "auto" }}>
				<div id="map" style={{ height: "500px", width: "100%", borderRadius: "15px" }}></div>
			</div>

			<div
				className="my-3"
				style={{
					background: "#ffffff",
					border: "1px solid #eef0f3",
					borderRadius: "16px",
					padding: "18px 22px",
					boxShadow: "0 1px 3px rgba(16, 24, 40, 0.04)",
				}}
			>
				<div className="d-flex justify-content-between align-items-center flex-wrap gap-3" style={{ marginBottom: 14 }}>
					<h4 style={{ fontWeight: 700, color: "#101828", margin: 0 }}>
						Viloyatlar kesimida tushum
					</h4>
					<div className="d-flex align-items-end gap-2">
						<div>
							<div style={{ fontSize: 11, color: "#98a2b3", marginBottom: 2 }}>Boshlanish oyi</div>
							<DatePicker
								selected={startMonth}
								onChange={(date) => setStartMonth(date)}
								dateFormat="MMMM yyyy"
								showMonthYearPicker
								showFullMonthYearPicker
								className="form-control form-control-sm"
								placeholderText="Boshlanish oyi"
								isClearable
							/>
						</div>
						<div style={{ paddingBottom: 8 }}>—</div>
						<div>
							<div style={{ fontSize: 11, color: "#98a2b3", marginBottom: 2 }}>Tugash oyi</div>
							<DatePicker
								selected={endMonth}
								onChange={(date) => setEndMonth(date)}
								dateFormat="MMMM yyyy"
								showMonthYearPicker
								showFullMonthYearPicker
								className="form-control form-control-sm"
								placeholderText="Tugash oyi"
								minDate={startMonth}
								isClearable
							/>
						</div>
					</div>
				</div>

				<div style={{ fontSize: 12, color: "#98a2b3", marginBottom: 8 }}>
					{visibleMonths.length
						? `Tanlangan davr: ${visibleMonths.length} oy (${visibleMonths[0]} … ${visibleMonths[visibleMonths.length - 1]})`
						: "Ma'lumot yo'q"}
				</div>

				<div className="table-responsive">
					<table className="table table-hover align-middle mb-0">
						<thead>
							<tr style={{ color: "#667085", fontSize: 13 }}>
								<th>Viloyat</th>
								<th className="text-center">Mijozlar</th>
								<th className="text-center">Faol</th>
								<th className="text-end">Hisoblangan tushum</th>
								<th className="text-end">To'langan</th>
							</tr>
						</thead>
						<tbody>
							{regionRows.map((row) => (
								<tr
									key={row.id}
									onClick={() => row.id !== "unknown" && setSelectedRegion(String(row.id))}
									style={{
										cursor: row.id === "unknown" ? "default" : "pointer",
										background: String(selectedRegion) === String(row.id) ? "#eff8ff" : undefined,
									}}
								>
									<td style={{ fontWeight: 600, color: "#101828" }}>{row.name}</td>
									<td className="text-center">{row.count.toLocaleString("ru-RU")}</td>
									<td className="text-center">
										<span style={{ color: "#067647", fontWeight: 600 }}>{row.active}</span>
										<span style={{ color: "#98a2b3" }}> / {row.count - row.active}</span>
									</td>
									<td className="text-end">{formatMoney(row.forMonth)} so'm</td>
									<td className="text-end" style={{ fontWeight: 600, color: "#067647" }}>{formatMoney(row.paidIn)} so'm</td>
								</tr>
							))}
						</tbody>
						<tfoot>
							<tr style={{ borderTop: "2px solid #eef0f3", fontWeight: 700, color: "#101828" }}>
								<td>Jami</td>
								<td className="text-center">{totals.count.toLocaleString("ru-RU")}</td>
								<td className="text-center">{totals.active} / {totals.count - totals.active}</td>
								<td className="text-end">{formatMoney(totals.forMonth)} so'm</td>
								<td className="text-end">{formatMoney(totals.paidIn)} so'm</td>
							</tr>
						</tfoot>
					</table>
				</div>
			</div>
		</div>
	);
};

export default MapComponent;
